import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Shield, AlertCircle, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";

interface MassEmailSettings {
  forceColdForLeadMassEmail?: boolean;
  forceColdForLeadDrip?: boolean;
  forceColdForDormantClientMassEmail?: boolean;
  dormantClientDays?: number;
  warmVolumeWarnThreshold?: number;
  lockColdOverrideForNonAdmins?: boolean;
}

interface SendingDomainLite {
  id: string;
  domain: string;
  purpose: "primary" | "cold";
  spfVerified: boolean;
  dkimVerified: boolean;
}

export function ColdEmailPolicyCard() {
  const { toast } = useToast();
  const { can } = useEffectivePermissions();
  // The cold email policy is read-only for all tenant users; only Clarik
  // platform/super-admins may change it (mirrored server-side on PATCH).
  const canEdit = can("platform.module.visible");
  const { data: settings, isLoading } = useQuery<MassEmailSettings>({
    queryKey: ["/api/admin/mass-email-settings"],
  });
  const { data: domainsRaw } = useQuery<SendingDomainLite[] | { domains?: SendingDomainLite[] }>({
    queryKey: ["/api/admin/sending-domains"],
  });
  const domains = Array.isArray(domainsRaw) ? domainsRaw : (domainsRaw?.domains ?? []);
  const verifiedColdDomain = domains.find(
    (d) => d.purpose === "cold" && d.spfVerified && d.dkimVerified,
  );

  const [forceLeadMass, setForceLeadMass] = useState(true);
  const [forceLeadDrip, setForceLeadDrip] = useState(true);
  const [forceDormant, setForceDormant] = useState(false);
  const [dormantDays, setDormantDays] = useState(180);
  const [warmVolumeThreshold, setWarmVolumeThreshold] = useState(20);
  const [lockOverride, setLockOverride] = useState(false);

  useEffect(() => {
    if (settings) {
      setForceLeadMass(settings.forceColdForLeadMassEmail ?? true);
      setForceLeadDrip(settings.forceColdForLeadDrip ?? true);
      setForceDormant(settings.forceColdForDormantClientMassEmail ?? false);
      setDormantDays(settings.dormantClientDays ?? 180);
      setWarmVolumeThreshold(settings.warmVolumeWarnThreshold ?? 20);
      setLockOverride(settings.lockColdOverrideForNonAdmins ?? false);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("PATCH", "/api/admin/mass-email-settings", {
        forceColdForLeadMassEmail: forceLeadMass,
        forceColdForLeadDrip: forceLeadDrip,
        forceColdForDormantClientMassEmail: forceDormant,
        dormantClientDays: Math.max(30, Math.min(1000, Number(dormantDays) || 180)),
        warmVolumeWarnThreshold: Math.max(1, Math.min(10000, Number(warmVolumeThreshold) || 20)),
        lockColdOverrideForNonAdmins: lockOverride,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/mass-email-settings"] });
      toast({ title: "Cold Email Policy saved" });
    },
    onError: (err: any) => {
      toast({
        title: "Could not save policy",
        description: err?.message || "Failed",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card data-testid="card-cold-email-policy">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Cold Email Policy</CardTitle>
          </div>
          <CardDescription>
            Workspace-wide rules for which audiences are routed through your dedicated cold-sending domain.
            Reps now see an explicit Cold/Warm toggle on every mass email and drip campaign. These policies
            set the safe default (lead/prospect lists start on Cold) and control whether reps may override it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Read-only notice for tenant users (only Clarik platform admins edit) */}
          {!canEdit && (
            <div
              className="flex items-start gap-3 p-3 rounded-md border border-border/60 bg-muted/40"
              data-testid="banner-policy-readonly"
            >
              <Lock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="text-xs text-muted-foreground">
                This cold email policy is managed by Clarik and shown here for reference. Settings are read-only —
                contact Clarik support if you need a change.
              </div>
            </div>
          )}

          {/* Cold-domain readiness */}
          <div
            className={`flex items-start gap-3 p-3 rounded-md border ${
              verifiedColdDomain
                ? "border-green-500/30 bg-green-500/5"
                : "border-amber-500/30 bg-amber-500/5"
            }`}
          >
            {verifiedColdDomain ? (
              <Badge variant="outline" className="text-green-600 border-green-500/30 mt-0.5">
                Ready
              </Badge>
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            )}
            <div className="text-xs text-muted-foreground">
              {verifiedColdDomain ? (
                <>
                  Cold sending domain <span className="font-mono text-foreground">{verifiedColdDomain.domain}</span> is verified.
                  Cold-routed sends will use this domain automatically.
                </>
              ) : (
                <>
                  No verified cold sending domain yet. Cold-routed sends will be blocked at preflight until you set one
                  up under <span className="font-medium">Email Settings → Sending Domains</span>.
                </>
              )}
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="force-lead-mass" className="text-sm font-medium">
                  Force cold for lead mass emails
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Any mass email targeting leads (new, not-now, interested, imported) routes through the cold pool
                  no matter what the rep selects.
                </p>
              </div>
              <Switch
                id="force-lead-mass"
                checked={forceLeadMass}
                onCheckedChange={setForceLeadMass}
                disabled={!canEdit}
                data-testid="switch-force-lead-mass"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="force-lead-drip" className="text-sm font-medium">
                  Force cold for lead drip campaigns
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Lead-triggered drips and lead-segment drips always use the cold pool.
                </p>
              </div>
              <Switch
                id="force-lead-drip"
                checked={forceLeadDrip}
                onCheckedChange={setForceLeadDrip}
                disabled={!canEdit}
                data-testid="switch-force-lead-drip"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="force-dormant" className="text-sm font-medium">
                  Force cold for dormant-client mass emails
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mass emails to clients who haven't booked recently are treated as cold (protects your transactional
                  reputation if a stale list is included).
                </p>
                {forceDormant && (
                  <div className="flex items-center gap-2 mt-2">
                    <Label htmlFor="dormant-days" className="text-xs text-muted-foreground whitespace-nowrap">
                      Dormant after
                    </Label>
                    <Input
                      id="dormant-days"
                      type="number"
                      min={30}
                      max={1000}
                      value={dormantDays}
                      onChange={(e) => setDormantDays(Number(e.target.value) || 180)}
                      disabled={!canEdit}
                      className="h-8 w-24"
                      data-testid="input-dormant-days"
                    />
                    <span className="text-xs text-muted-foreground">days since last booking</span>
                  </div>
                )}
              </div>
              <Switch
                id="force-dormant"
                checked={forceDormant}
                onCheckedChange={setForceDormant}
                disabled={!canEdit}
                data-testid="switch-force-dormant"
              />
            </div>
          </div>

          {/* Override controls */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="lock-override" className="text-sm font-medium">
                  Lock cold/warm override for non-admins
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When on, only admins can switch a lead/prospect send from Cold to Warm. Reps still see the
                  toggle but can't move a forced-cold audience onto your warm (inbox) domain.
                </p>
              </div>
              <Switch
                id="lock-override"
                checked={lockOverride}
                onCheckedChange={setLockOverride}
                disabled={!canEdit}
                data-testid="switch-lock-override"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="warm-volume-threshold" className="text-sm font-medium">
                  Warm high-volume warning threshold
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sending a Warm campaign to this many recipients (or more) forces a deliberate acknowledgement
                  step before the send goes out. Protects your inbox reputation from accidental large warm blasts.
                </p>
              </div>
              <Input
                id="warm-volume-threshold"
                type="number"
                min={1}
                max={10000}
                value={warmVolumeThreshold}
                onChange={(e) => setWarmVolumeThreshold(Number(e.target.value) || 20)}
                disabled={!canEdit}
                className="h-8 w-24 shrink-0"
                data-testid="input-warm-volume-threshold"
              />
            </div>
          </div>

          {canEdit && (
            <div className="flex justify-end pt-2 border-t">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                data-testid="button-save-cold-policy"
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save policy
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
